import { inject, Injectable } from '@angular/core';
import { HttpUtils } from '@core/utils/http-utils';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngxs/store';
import { EditCurrencyDialogComponent } from '../components/pricing-partner/edit-currency-dialog/edit-currency-dialog.component';
import { EditNotesDialogComponent } from '../components/pricing-partner/edit-notes-dialog/edit-notes-dialog.component';
import { PricingPartnerDialogComponent } from '../components/pricing-partner/pricing-partner-dialog/pricing-partner-dialog.component';
import { UpdatePricingPartnerAction } from '../store/pricing-partner.actions';
import {
  PricingPartnerFilters,
  PricingPartnerStateModel,
} from '../store/pricing-partner.state';
import {
  BundlePrice,
  BundlePriceApiModelData,
  BundlePricePivot,
  BundlePricePivotApiModelData,
  PriceRequestApiModelData,
  PricingPartner,
  PricingPartnerApiModelData,
} from '../types/pricing.types';
import { DiscountUtilsService } from './discount-utils.service';

@Injectable({
  providedIn: 'root',
})
export class PricingPartnerUtilsService {
  private ngbModal = inject(NgbModal);
  private store = inject(Store);
  private discountUtilsService = inject(DiscountUtilsService);

  public openPricingPartnerDialog(id: string) {
    const modalRef = this.ngbModal.open(PricingPartnerDialogComponent, {
      centered: true,
      modalDialogClass: 'modal-size-xxl',
    });

    modalRef.componentInstance.partnerId.set(id);
  }

  public openEditCurrencyDialog(id: string) {
    const modalRef = this.ngbModal.open(EditCurrencyDialogComponent, {
      centered: true,
      size: 'modal-size-md',
    });

    modalRef.componentInstance.currency.set(
      this.getPricingPartnerSnapshot(id)?.currency ?? null
    );
    modalRef.componentInstance.onSave.set((currency: string) =>
      this.store.dispatch(new UpdatePricingPartnerAction(id, { currency }))
    );
  }

  public openEditNotesDialog(id: string) {
    const modalRef = this.ngbModal.open(EditNotesDialogComponent, {
      centered: true,
      size: 'modal-size-md',
    });

    modalRef.componentInstance.notes.set(
      this.getPricingPartnerSnapshot(id)?.notes ?? null
    );
    modalRef.componentInstance.onSave.set((notes: string) =>
      this.store.dispatch(new UpdatePricingPartnerAction(id, { notes }))
    );
  }

  // Reads the store without importing `PricingPartnerState`, which would
  // create a circular import (state -> service -> utils -> state).
  private getPricingPartnerSnapshot(id: string): PricingPartner | undefined {
    return this.store.selectSnapshot(
      (state: { pricing: PricingPartnerStateModel }) =>
        state.pricing?.pricingPartners?.[id]
    );
  }

  public buildPricingPartnerHttpParams(params: PricingPartnerFilters) {
    let httpParams = HttpUtils.buildHttpParams();

    if (params.partnerIds?.length) {
      httpParams = httpParams.set('partner_ids', params.partnerIds.join(','));
    }

    if (params.name) {
      httpParams = httpParams.set('name', params.name);
    }

    if (params.limit) {
      httpParams = httpParams.set('limit', params.limit);
    }

    if (params.page) {
      httpParams = httpParams.set('page', params.page);
    }

    if (params.countries?.length) {
      httpParams = httpParams.set('countries', params.countries.join(','));
    }

    if (params.currency) {
      httpParams = httpParams.set('currency', params.currency);
    }

    if (params.notes) {
      httpParams = httpParams.set('notes', params.notes);
    }

    if (
      params.hasPriceRequests !== null &&
      params.hasPriceRequests !== undefined
    ) {
      httpParams = httpParams.set(
        'has_missing_prices',
        params.hasPriceRequests
      );
    }

    if (
      params.hasPendingRequests !== null &&
      params.hasPendingRequests !== undefined
    ) {
      httpParams = httpParams.set(
        'has_pending_price_requests',
        params.hasPendingRequests
      );
    }

    if (params.active !== null && params.active !== undefined) {
      httpParams = httpParams.set('active', params.active);
    }

    if (params.paymentTypes?.length) {
      httpParams = httpParams.set(
        'payment_types',
        params.paymentTypes.join(',')
      );
    }

    return httpParams;
  }

  public apiModelToPricingPartnerBundlePivot(
    model: BundlePricePivotApiModelData
  ): BundlePricePivot {
    return {
      partnerId: model.partner_id,
      bundleId: model.bundle_id,
      id: model.id,
      price: model.price ?? null,
      validFrom: new Date(model.valid_from),
      generatedBy: model.generated_by ?? null,
    };
  }

  public apiModelToPricingPartnerBundle(
    model: BundlePriceApiModelData
  ): BundlePrice | null {
    if (!model?.pivot) {
      return null;
    }

    return {
      id: model.id,
      name: model.name,
      internalName: model.internal_name,
      discountedPrice: model.discounted_price ?? null,
      discount: model.discount
        ? this.discountUtilsService.apiModelToDiscount(model.discount)
        : null,
      pivot: this.apiModelToPricingPartnerBundlePivot(model.pivot),
      isPriceRequest: false,
      // decimal string from the backend (e.g. "120.00")
      countryEupPrice:
        model.country_eup_price != null ? Number(model.country_eup_price) : null,
      countryEupDiscountedPrice:
        model.country_eup_discounted_price != null
          ? Number(model.country_eup_discounted_price)
          : null,
      countryEupValidFrom: model.country_eup_valid_from
        ? new Date(model.country_eup_valid_from)
        : null,
    };
  }

  public apiModelToPricingPartnerPriceRequest(
    model: PriceRequestApiModelData
  ): BundlePrice | null {
    if (!model) {
      return null;
    }

    return {
      id: model.id,
      name: model.bundle?.name ?? model.name ?? '',
      internalName:
        model.bundle?.internal_name ??
        model.internal_name ??
        model.bundle?.name ??
        model.name ??
        '',
      discountedPrice: model.discounted_price ?? null,
      discount: model.discount
        ? this.discountUtilsService.apiModelToDiscount(model.discount)
        : null,
      pivot: {
        id: model.id,
        partnerId: model.partner_id,
        bundleId: model.bundle_id,
        price: model.price ?? null,
        validFrom: new Date(model.valid_from),
        generatedBy: model.created_by ?? null,
      },
      isPriceRequest: true,
    };
  }

  public apiModelToPricingPartner(
    model: PricingPartnerApiModelData
  ): PricingPartner {
    return {
      id: model.id,
      name: model.name,
      accountManagerId: model.account_manager_id ?? null,
      country: model.country,
      email: model.email,
      phone: model.phone,
      currency: model.currency ?? null,
      notes: model.notes ?? null,
      active: model.active,
      missingPrices: model.missing_prices ?? null,
      pendingPriceRequests: model.pending_price_requests ?? null,
      pricelist: model.pricelist?.length
        ? model.pricelist
            .map((bundlePrice: BundlePriceApiModelData) =>
              this.apiModelToPricingPartnerBundle(bundlePrice)
            )
            .filter((bundlePrice) => !!bundlePrice)
        : [],
      priceRequests: model.price_requests?.length
        ? model.price_requests
            .map((priceRequest: PriceRequestApiModelData) =>
              this.apiModelToPricingPartnerPriceRequest(priceRequest)
            )
            .filter((priceRequest) => !!priceRequest)
        : [],
      paymentTypes: model.payment_types ?? [],
    };
  }

  public downloadFile(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }
}
